import { cn } from "@/lib/utils";
import { Home, MessageSquare, BookText, Shield, BookOpen, User, Settings, CreditCard, LogOut, LogIn } from "lucide-react";
import { FloatingDockDesktop } from "./navigation/FloatingDockDesktop";
import { FloatingDockMobile } from "./navigation/FloatingDockMobile";
import { NavItem } from "./navigation/types";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useEffect, useState } from "react";
import { Session } from "@supabase/supabase-js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Link } from "react-router-dom";

const navItems: NavItem[] = [
  { title: "Home", icon: <Home className="h-full w-full" />, href: "/" },
  { title: "Chat", icon: <MessageSquare className="h-full w-full" />, href: "/chat" },
  { title: "Journal", icon: <BookText className="h-full w-full" />, href: "/journal" },
  { title: "Safety Plan", icon: <Shield className="h-full w-full" />, href: "/safety-plan" },
  { title: "Resources", icon: <BookOpen className="h-full w-full" />, href: "/resources" },
];

export const FloatingDock = ({
  desktopClassName,
  mobileClassName,
}: {
  desktopClassName?: string;
  mobileClassName?: string;
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      navigate("/login");
      toast({
        title: "Signed out successfully",
        description: "You have been logged out of your account",
      });
    } catch (error) {
      toast({
        title: "Error signing out",
        description: "There was a problem signing out. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="fixed top-4 left-0 right-0 z-50">
      <div className="flex justify-between items-center max-w-screen-xl mx-auto px-4">
        <FloatingDockDesktop items={navItems} className={desktopClassName} />
        <FloatingDockMobile items={navItems} className={mobileClassName} />
        
        <div className="bg-gray-50 dark:bg-neutral-900 rounded-full p-2 shadow-lg border">
          {session ? (
            <DropdownMenu>
              <DropdownMenuTrigger className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-primary/90 hover:text-primary-foreground transition-colors">
                <User className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="flex items-center cursor-pointer">
                    <Settings className="mr-2 h-4 w-4" />
                    Profile Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/subscription" className="flex items-center cursor-pointer">
                    <CreditCard className="mr-2 h-4 w-4" />
                    Subscription
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link 
              to="/login"
              className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-primary/90 hover:text-primary-foreground transition-colors"
            >
              <LogIn className="h-4 w-4" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default FloatingDock;